import { autoinject, bindable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AuthService } from 'app/um/services/auth-service';
import { guid } from 'app/shared/lib/guid';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IExtIqcSettings } from 'app/ext/iqc/ext-iqc';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { debounce } from 'app/shared/lib/decorators';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import * as UM from 'app/um/models/essette-api';

export type HasReviewsFlag = { hasReviews?: boolean };
type AuthDiagnosisWithLocalState = ExtendLocalState<UM.AuthDiagnosis & HasReviewsFlag, { isExpanded?: boolean; }>;

const expandedDiagnosesCache = new Map<string, Array<AuthDiagnosisWithLocalState>>();

@autoinject()
export class IqcSummaryDiagnosesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected auth?: UM.Auth;

    protected expandedCacheKey = "iqc-summary-diagnoses-grid";

    protected settings: IExtIqcSettings = {
        showOpenReviewSummaryPdf: true,
        showOpenBookView: false,
        allowReviewCopy: false,
        useLatest: true
    };

    protected detailSettings: IExtIqcSettings = {
        showOpenReviewSummaryPdf: true,
        showOpenBookView: false,
        allowReviewCopy: false,
        useLatest: false
    };

    protected diagnoses: AuthDiagnosisWithLocalState[] = [];
    protected resultCount = 0;
    protected sorting = new Sorting();
    protected pageSize = 25;
    public allExpanded = true;
    public allCollapsed = false;

    protected diagnosesDetails: Record<string, boolean> = {};

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<AuthDiagnosisWithLocalState[]>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected service: AuthService,
        private readonly queue: TaskQueue,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(this.env.application.code === 'Portal.Providers') {
            this.settings.useDynamicSso = this.detailSettings.useDynamicSso = true;
            this.settings.useLatest = false;
        }
        this.sorting.add({
            reference: 'isPrimary',
            direction: SortDirection.Descending
        });
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadDiagnoses(false);
    }

    protected updateCollapsed(event: ExpandEvent<AuthDiagnosisWithLocalState>): void {
        const item = event.detail.item;

        if(!item?.id) return;
        const foundDiagnosis = this.diagnoses.find(d => d.id === item.id)
        if(foundDiagnosis?._state) {
            foundDiagnosis._state.isExpanded = !foundDiagnosis._state.isExpanded;
        }

        this.allCollapsed = this.diagnoses.every(d => !d._state?.isExpanded);
        this.allExpanded = this.diagnoses.every(d => d._state?.isExpanded);

        this.diagnosesDetails[item.id!] = true;
    }

    @debounce()
    public expandAll(): void {
        this.diagnosesDetails = {};
        this.diagnoses.forEach((d: AuthDiagnosisWithLocalState) => {
            if(d.hasReviews) this.diagnosesDetails[d.id!] = true;
        })
        this.diagnoses.forEach((d: AuthDiagnosisWithLocalState) => {
            d._state = { isExpanded: true };
        })
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedDiagnoses = expandedDiagnosesCache.get(this.expandedCacheKey);
            if(expandedDiagnoses !== undefined) {
                this.$table.restoreExpanded(expandedDiagnoses);
            }
        });
        this.allExpanded = true;
        this.allCollapsed = false;
    }

    public collapseAll(): void {
        this.diagnosesDetails = {};
        this.diagnoses.forEach((d: AuthDiagnosisWithLocalState) => {
            d._state = { isExpanded: false };
        })
        if(this.$table) this.$table.closeAll();
        this.allExpanded = false;
        this.allCollapsed = true;
    }

    private restoreExpanded() {
        // we need to give aurelia a chance to bind the goals into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) return;
            const expandedDiagnoses = expandedDiagnosesCache.get(this.expandedCacheKey);
            if(expandedDiagnoses !== undefined) {
                this.$table.restoreExpanded(expandedDiagnoses);
            }
        });
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadDiagnoses();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadDiagnoses(expandDetails: boolean = true): Promise<void> {
        if(!this.auth?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'definition',
            'flags',
            'medicalReview'
        ];

        const fields = [
            'definition.code',
            'definition.description',
            'flags.description',
            'isPrimary',
            'medicalReview.*'
        ];

        if(this.env.application.code === 'Portal.Providers') {
            includes.push('medicalReview.history.medicalReviews');
            fields.push('medicalReview.history.medicalReviews.*');
        }

        try {
            this.state.beginLoad();

            const response = await this.service.loadDiagnosesByAuth(this.auth.id, options, includes, fields);
            this.diagnoses = response.results;
            this.diagnoses.forEach(d => {
                d.hasReviews = !!d.medicalReview;
                // if in portal and medical review has been overwritten, retrieve appropriate external identifier from history
                if(this.env.application.code === 'Portal.Providers' && d.medicalReview?.identifier) {
                    const medicalReviews = d.medicalReview.history?.medicalReviews;
                    const foundPortalMedicalReview = medicalReviews?.find(review => review.createdInPortal);
                    if(foundPortalMedicalReview) d.medicalReview.identifier.external = foundPortalMedicalReview.identifier?.external;
                    else d.hasReviews = false;
                }
            });

            this.resultCount = response.count || 0;

            if(this.diagnoses.some(d => d.hasReviews) && expandDetails) {
                this.expandAll();
                expandedDiagnosesCache.set(this.expandedCacheKey, this.diagnoses.filter(d => d.hasReviews));
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
